import { Link } from "react-router-dom";
import { PageHeader } from "@/shared/components/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { StatusText } from "@/shared/components/ui/status-chip";
import { TableCard, TableHeadRow, TableRow, Th, Empty } from "@/shared/components/ui/table";
import { EmptyState } from "@/shared/components/ui/empty-state";
import { useOutbox } from "./use-global";

const GRID = "grid-cols-[1fr_1.5fr_2fr_2fr_1fr]";

/** Outbox (global nav) — recent delivery events streaming back across sequences. */
export function OutboxPage() {
  const { data, isLoading, isError, refetch } = useOutbox();
  const rows = data ?? [];

  if (isError) {
    return (
      <div>
        <PageHeader title="Outbox" />
        <EmptyState
          message="Couldn't load delivery events."
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }
  if (!isLoading && rows.length === 0) {
    return (
      <div>
        <PageHeader title="Outbox" />
        <EmptyState message="No delivery events yet. Once a launched sequence sends, provider events (sent, opened, replied) stream in here." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Outbox"
        footnote="Delivery events from the provider across every launched sequence — newest first."
      />
      <TableCard>
        <TableHeadRow grid={GRID}>
          <Th>Event</Th>
          <Th>When</Th>
          <Th>Contact</Th>
          <Th>Campaign / Sequence</Th>
          <Th>Step</Th>
        </TableHeadRow>
        {isLoading ? (
          <SkeletonRows />
        ) : (
          rows.map((e) => (
            <TableRow key={e.id} grid={GRID} data-testid={`event-row-${e.id}`}>
              <div>
                <StatusText tone={eventTone(e.type)}>{e.type}</StatusText>
              </div>
              <div className="font-mono text-[11px] text-ge-secondary">{formatWhen(e.at)}</div>
              <div className="min-w-0">
                <div className="truncate font-mono text-[11.5px] text-ge-value">
                  {e.contactEmail ?? <Empty />}
                </div>
                {e.leadName && (
                  <div className="truncate text-[11px] text-ge-faint">{e.leadName}</div>
                )}
              </div>
              <div className="min-w-0">
                {e.campaignId ? (
                  <Link
                    to={`/campaigns/${e.campaignId}`}
                    className="truncate font-sans text-[12.5px] font-semibold text-ge-primary hover:text-ge-accent"
                  >
                    {e.campaignName ?? e.campaignId}
                  </Link>
                ) : (
                  <Empty />
                )}
                {e.sequenceName && (
                  <div className="truncate font-mono text-[11px] text-ge-faint">{e.sequenceName}</div>
                )}
              </div>
              <div className="font-mono text-[12px] text-ge-value">
                {e.stepOrder != null ? `#${e.stepOrder}` : <Empty />}
              </div>
            </TableRow>
          ))
        )}
      </TableCard>
    </div>
  );
}

function eventTone(type: string): "accent" | "value" | "warning" | "danger" | "faint" {
  if (type === "REPLY") return "accent";
  if (type === "OPEN" || type === "CLICK") return "value";
  if (type === "BOUNCE" || type === "UNSUBSCRIBE") return "danger";
  return "faint"; // SENT
}

function formatWhen(iso: string): string {
  // ISO datetime → "YYYY-MM-DD HH:mm" (no locale parsing needed).
  return iso.length >= 16 ? `${iso.slice(0, 10)} ${iso.slice(11, 16)}` : iso.slice(0, 10);
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <TableRow key={i} grid={GRID} aria-hidden data-testid="event-skeleton">
          {Array.from({ length: 5 }).map((__, j) => (
            <div key={j} className="h-[12px] w-14 animate-pulse rounded bg-ge-border-card" />
          ))}
        </TableRow>
      ))}
    </>
  );
}
